import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarWithUpload } from 'components/modules/user/AvatarWithUpload';
import ChangePasswordModal from 'components/modules/user/change-password-modal';
import Sidebar from 'components/modules/user/warehouse/Sidebar';
import SessionStore from 'stores/SessionStore';
import Container from 'components/v2/container';
import CargomaticCard from 'components/v2/card';

import { titleize } from 'underscore.string';
import CargomaticInput from 'components/v2/input';
import PhoneField from 'components/v2/forms/PhoneField';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import CustomersAPIUtils, { getCustomer } from 'utils/CustomersAPIUtils';
import validator from 'email-validator';
import { isEmpty, isEqual } from 'lodash';

const pageTestKey = 'warehouse-portal-profile';

const Profile = () => {
  const { t } = useTranslation();
  const [warehouseUser, setWarehouseUser] = useState({});
  const [initWarehouseUser, setInitiWarehouseUser] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [displayName, setDisplayName] = useState('');

  const fetchUser = async () => {
    const user = SessionStore.getUser();
    const result = await getCustomer(user._id);
    if (result?.entity) {
      setInitiWarehouseUser(result.entity);
      setWarehouseUser(result.entity);
      const { first_name, last_name } = result.entity;
      setDisplayName(`${first_name} ${last_name}`);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const saveDisabled =
    isEmpty(warehouseUser?.first_name?.trim()) ||
    isEmpty(warehouseUser?.last_name?.trim()) ||
    isEmpty(warehouseUser?.phone?.trim()) ||
    (isEqual(
      warehouseUser?.first_name?.trim(),
      initWarehouseUser?.first_name?.trim(),
    ) &&
      isEqual(
        warehouseUser?.last_name?.trim(),
        initWarehouseUser?.last_name?.trim(),
      ) &&
      isEqual(warehouseUser?.phone?.trim(), initWarehouseUser?.phone?.trim()) &&
      isEqual(
        warehouseUser?.email?.trim(),
        initWarehouseUser?.email?.trim(),
      )) ||
    !validator.validate(warehouseUser.email);

  const handleInputChange = (field, value) => {
    setWarehouseUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = (newPassword) => {
    CustomersActionCreators.updatePassword(warehouseUser._id, newPassword);
  };

  const handleSave = async () => {
    const existingFields = ['first_name', 'last_name', 'phone', 'email'];
    const updates = {};
    existingFields.forEach((k) => {
      if (warehouseUser[k].trim() !== initWarehouseUser[k].trim()) {
        updates[k] = warehouseUser[k].trim();
      }
    });
    if (Object.keys(updates).length) {
      updates._id = warehouseUser._id;
      // Make Api Call
      const result = await CustomersAPIUtils.updateWarehouseUser(updates);
      if (result?.success && result?.user) {
        setWarehouseUser(result.user);
      }
    }
  };

  const renderPhoto = () => {
    const userId = warehouseUser?._id;
    return (
      <div data-testid={`${pageTestKey}-avatar`} className="dropdown-wrapper">
        <AvatarWithUpload
          key={userId}
          userId={userId}
          style={{
            borderWidth: 2,
            borderColor: '#5cb85c',
            borderStyle: 'dashed',
            borderRadius: 5,
            display: 'inline-block',
            padding: 0,
            textAlign: 'center',
          }}
        />
      </div>
    );
  };
  const {
    first_name = '',
    last_name = '',
    phone = '',
    email = '',
  } = warehouseUser;
  return (
    <Container className="lg:mt-0 mt-4">
      <div
        data-tesid={`${pageTestKey}-page`}
        className="lg:grid lg:grid-cols-12 lg:gap-6 mt-10"
      >
        <div className="lg:col-span-3">
          <Sidebar activePage="profile" />
        </div>
        <div data-testid={`${pageTestKey}-container`} className="lg:col-span-6">
          <CargomaticCard
            header={() => (
              <div className="flex items-center justify-between">
                <h3 className="my-0">
                  {t('WELCOME')}, &nbsp; {displayName}
                </h3>
              </div>
            )}
            footer={() => (
              <div
                data-testid={`${pageTestKey}-footer`}
                className="form-actions"
              >
                <button
                  data-testid={`${pageTestKey}-save-button`}
                  disabled={saveDisabled}
                  className="button button-green"
                  onClick={handleSave}
                >
                  {titleize(t('SAVE'))}
                </button>
              </div>
            )}
            showFooter
            grayFooter
          >
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-6 sm:col-span-3">
                <CargomaticInput
                  id={`${pageTestKey}-first-name`}
                  label={titleize(t('FIRST_NAME'))}
                  type="text"
                  className=""
                  placeholder={t('ENTER_FIRST_NAME')}
                  value={first_name}
                  onChange={(val) => handleInputChange('first_name', val)}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <CargomaticInput
                  id={`${pageTestKey}-last-name`}
                  label={titleize(t('LAST_NAME'))}
                  className=""
                  type="text"
                  placeholder={t('ENTER_LAST_NAME')}
                  value={last_name}
                  onChange={(val) => handleInputChange('last_name', val)}
                />
              </div>
            </div>

            {/* Phone and extension */}
            <div className="grid grid-cols-6 gap-4 mt-3">
              <div
                data-testid={`${pageTestKey}-phone`}
                className="col-span-6 sm:col-span-3"
              >
                <label className="mb-1 form-label">
                  {titleize(t('PHONE'))}
                </label>
                <PhoneField
                  id="carrier-phone"
                  containerClassName="mt-3"
                  onChange={(val) => handleInputChange('phone', val)}
                  value={phone}
                />
              </div>
              <div className="col-span-6 sm:col-span-4">
                <CargomaticInput
                  id={`${pageTestKey}-email`}
                  label={titleize(t('EMAIL'))}
                  type="email"
                  className=""
                  value={email}
                  placeholder={t('ENTER_EMAIL')}
                  onChange={(val) => handleInputChange('email', val)}
                />
              </div>
            </div>
          </CargomaticCard>
        </div>

        <div className="lg:col-span-3 col-auto space-y-4">
          {renderPhoto()}
          <div style={{ marginTop: '1em' }}>
            <button
              data-testid={`${pageTestKey}-change-password-button`}
              className="button button-red"
              onClick={() => setShowChangePasswordModal(true)}
            >
              {t('CHANGE_PASSWORD')}
            </button>
          </div>
        </div>
        <ChangePasswordModal
          show={showChangePasswordModal}
          onHide={() => setShowChangePasswordModal(false)}
          onSave={handleChangePassword}
        />
      </div>
    </Container>
  );
};

export default Profile;
